import { useState, useCallback } from 'react';
import {
  AddIcon,
  Alert,
  Button,
  CheckIcon,
  Chip,
  DeleteIcon,
  Input,
  Spinner,
  Text,
  Tooltip,
} from '@billda/design-system';

import { format, parseISO } from 'date-fns';
import { PotSummaryCard } from '../PotSummaryCard';
import {
  useAddRejectionReason,
  useResolveRejectionReason,
  useRemoveRejectionReason,
} from '../../hooks/useRejectionReasons';
import type {
  PotDetailsDisplayModel,
  PotRejectionReason,
} from '../../types/potDetails.types';

interface RejectionManagementPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onStatusChange?: () => void;
}

export function RejectionManagementPanelContent({
  potDetails,
  onStatusChange,
}: RejectionManagementPanelContentProps) {
  const [newReason, setNewReason] = useState('');

  const { mutate: addReason, isPending: isAdding } = useAddRejectionReason();

  const { mutate: resolveReason, isPending: isResolving } =
    useResolveRejectionReason();

  const { mutate: removeReason, isPending: isRemoving } =
    useRemoveRejectionReason();

  const isPending = isAdding || isResolving || isRemoving;

  const handleAddReason = useCallback(() => {
    const trimmedReason = newReason.trim();
    if (trimmedReason) {
      addReason(
        { potId: potDetails.id, reason: trimmedReason },
        {
          onSuccess: response => {
            setNewReason('');
            if (response.status !== potDetails.approvals.status) {
              onStatusChange?.();
            }
          },
        }
      );
    }
  }, [
    addReason,
    newReason,
    potDetails.id,
    potDetails.approvals.status,
    onStatusChange,
  ]);

  const handleResolveReason = useCallback(
    (reasonId: string) => {
      resolveReason(
        { potId: potDetails.id, reasonId },
        {
          onSuccess: response => {
            if (response.status !== potDetails.approvals.status) {
              onStatusChange?.();
            }
          },
        }
      );
    },
    [resolveReason, potDetails.id, potDetails.approvals.status, onStatusChange]
  );

  const handleRemoveReason = useCallback(
    (reasonId: string) => {
      removeReason(
        { potId: potDetails.id, reasonId },
        {
          onSuccess: response => {
            if (response.status !== potDetails.approvals.status) {
              onStatusChange?.();
            }
          },
        }
      );
    },
    [removeReason, potDetails.id, potDetails.approvals.status, onStatusChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddReason();
      }
    },
    [handleAddReason]
  );

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), 'dd MMM yyyy, HH:mm');
  };

  const unresolvedReasons = potDetails.rejectionReasons.filter(
    r => !r.isResolved
  );
  const resolvedReasons = potDetails.rejectionReasons.filter(r => r.isResolved);

  const canAddReason = newReason.trim().length > 0;

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col gap-6">
          {/* Summary Card */}
          <PotSummaryCard
            ownerType={potDetails.owner.ownerType}
            ownerName={potDetails.owner.ownerName}
            period={potDetails.period}
          />

          {/* Status Summary */}
          <Alert
            severity={unresolvedReasons.length > 0 ? 'warning' : 'success'}
            icon={false}
          >
            <div className="flex items-center gap-4">
              <Chip
                size="sm"
                color={unresolvedReasons.length > 0 ? 'warning' : 'success'}
                label={`${unresolvedReasons.length} unresolved`}
              />
              {resolvedReasons.length > 0 && (
                <Chip
                  size="sm"
                  color="default"
                  label={`${resolvedReasons.length} resolved`}
                />
              )}
            </div>
          </Alert>

          {/* Add New Reason */}
          <div>
            <Text variant="body2" className="mb-2 font-semibold">
              Add New Reason
            </Text>
            <div className="flex gap-2">
              <Input
                inputSize="sm"
                placeholder="Enter a new rejection reason..."
                value={newReason}
                onChange={e => setNewReason(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={1000}
                data-testid="management-reason-input"
                disabled={isPending}
              />
              <Button
                variant="outline"
                onClick={handleAddReason}
                disabled={!canAddReason || isPending}
                className="px-4"
                aria-label="Add reason"
                data-testid="management-add-reason-button"
              >
                {isAdding ? (
                  <Spinner size={20} color="inherit" />
                ) : (
                  <AddIcon aria-hidden="true" className="size-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Unresolved Reasons */}
          {unresolvedReasons.length > 0 && (
            <div>
              <Text variant="body2" className="mb-2 font-semibold">
                Unresolved Reasons ({unresolvedReasons.length})
              </Text>
              <ul className="m-0 flex list-none flex-col gap-2 rounded-lg bg-surface-raised p-2">
                {unresolvedReasons.map(reason => (
                  <ReasonListItem
                    key={reason.id}
                    reason={reason}
                    onResolve={() => {
                      handleResolveReason(reason.id);
                    }}
                    onRemove={() => {
                      handleRemoveReason(reason.id);
                    }}
                    formatDateTime={formatDateTime}
                    disabled={isPending}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Resolved Reasons */}
          {resolvedReasons.length > 0 && (
            <div>
              <Text
                variant="body2"
                color="secondary"
                className="mb-2 font-semibold"
              >
                Resolved Reasons ({resolvedReasons.length})
              </Text>
              <ul className="m-0 flex list-none flex-col gap-2 rounded-lg bg-surface-raised p-2">
                {resolvedReasons.map(reason => (
                  <ReasonListItem
                    key={reason.id}
                    reason={reason}
                    onRemove={() => {
                      handleRemoveReason(reason.id);
                    }}
                    formatDateTime={formatDateTime}
                    disabled={isPending}
                    isResolved
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Info footer */}
      {unresolvedReasons.length === 0 && (
        <div className="border-t border-border-default bg-surface-default p-6">
          <Alert severity="success" data-testid="all-reasons-resolved-alert">
            All rejection reasons have been resolved. The pot has transitioned
            to Verified status.
          </Alert>
        </div>
      )}
    </div>
  );
}

interface ReasonListItemProps {
  reason: PotRejectionReason;
  onResolve?: () => void;
  onRemove: () => void;
  formatDateTime: (date: string) => string;
  disabled: boolean;
  isResolved?: boolean;
}

function ReasonListItem({
  reason,
  onResolve,
  onRemove,
  formatDateTime,
  disabled,
  isResolved = false,
}: ReasonListItemProps) {
  return (
    <li
      className={
        isResolved
          ? 'flex items-center justify-between gap-2 line-through opacity-70'
          : 'flex items-center justify-between gap-2'
      }
    >
      <div className="min-w-0">
        <Text
          variant="body2"
          className={isResolved ? 'line-through' : undefined}
        >
          {reason.reason}
        </Text>
        <div className="flex flex-col">
          <Text as="span" variant="caption" color="secondary">
            Added by {reason.createdByName} on{' '}
            {formatDateTime(reason.createdAt)}
          </Text>
          {isResolved && reason.resolvedAt && (
            <Text as="span" variant="caption" className="text-content-success">
              Resolved by {reason.resolvedByName} on{' '}
              {formatDateTime(reason.resolvedAt)}
            </Text>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        {!isResolved && onResolve && (
          <Tooltip content="Mark as resolved">
            <span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-content-success"
                onClick={onResolve}
                disabled={disabled}
                data-testid="management-resolve-button"
              >
                <CheckIcon aria-hidden="true" className="size-4" />
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip content="Remove reason">
          <span>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-content-error"
              onClick={onRemove}
              disabled={disabled}
              data-testid="management-remove-button"
            >
              <DeleteIcon aria-hidden="true" className="size-4" />
            </Button>
          </span>
        </Tooltip>
      </div>
    </li>
  );
}
